/**
 * Rules and Regulations Section
 *
 * CURRENT STATUS: Partial implementation - view-only for most fields
 *
 * EDIT MODE REQUIREMENTS:
 * The following fields need select dropdowns (currently display-only):
 * - USCG Group: Use useGetUSCGGroups() hook (API: GET /api/GetUscgGroups)
 * - IMDG Class: Use useGetIMDGCodeClasses() hook (API: GET /api/GetImdgCodeClasses)
 * - FOSFA: Enum select with values [0: Not Listed, 1: Listed, 2: Banned, 3: Restricted]
 * - EU: Enum select with values [0: Not Listed, 1: Listed, 2: Banned]
 * - NIOP: Enum select with values [0: Not Listed, 1: Listed, 2: Restricted, 3: Prohibited]
 * - OPA90: Boolean select [true: Yes, false: No]
 *
 * IBC Annex II fields (editable only when product.id === product.categoryId):
 * - All IBC fields need enum selects (see old template for enum values)
 * - Fire Protection: Needs checkboxes (A, B, C, D)
 *
 * Tripartite Agreement (currently using text inputs, should use):
 * - Expiry Date: Date picker component
 *
 * API HOOKS AVAILABLE:
 * - ✅ useGetUSCGGroups() - Created in src/api/useGetUSCGGroups.tsx
 * - ✅ useGetIMDGCodeClasses() - Created in src/api/useGetIMDGCodeClasses.tsx
 *
 * TODO:
 * 1. Create CompactSelect styled component
 * 2. Implement edit mode with select dropdowns for all enum fields
 * 3. Add category-level edit mode for IBC fields (when isProductCategory)
 * 4. Replace Tripartite Agreement Expiry Date text input with date picker
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useGetIMDGCodeClasses } from '../../../../api/useGetIMDGCodeClasses';
import { useGetUSCGGroups } from '../../../../api/useGetUSCGGroups';
import { useAuthStore } from '../../../../context/AuthContext';
import { useEditMode } from '../../../../context/EditModeContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import { ProductDetail } from '../../../../types/ProductTypes';
import { CompactSelect, CompactInput100 } from '../../EditableFields.styles';
import { SidebarCard, SidebarTitle, SidebarContent, SidebarItem } from '../../Sidebar.styles';
import {
    formatUSCGGroup,
    formatIMDGClass,
    formatAllowance,
    formatNIOPAllowance,
    formatYesNo,
    formatDate,
    formatMarpolRegulation,
    formatPollutionCategory,
    formatIBCHazards,
    formatShipType,
    formatTankType,
    formatTankVents,
    formatEnvironmentalControl,
    formatGauging,
    formatVapourDetection,
    formatFireProtection,
} from '../../../../utils/regulationFormatters';

interface RulesRegulationsSectionProps {
    product: ProductDetail;
}

const RulesRegulationsSection: React.FC<RulesRegulationsSectionProps> = ({ product }) => {
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;
    const getPendingChanges = productEditContext?.getPendingChanges;
    const showEditUI = canEdit && isInEditMode;

    // Check if this is a product category (product.id === product.categoryId)
    const isProductCategory = product.id === product.categoryId;

    // Fetch dropdown data
    const { uscgGroups } = useGetUSCGGroups();
    const { imdgCodeClasses } = useGetIMDGCodeClasses();

    const regs = product.regulations;
    const categoryRegs = product.category?.regulations;
    const tripartite = regs?.tripartiteAgreement;
    const ibc = categoryRegs?.ibcRegulation;
    const marpolReg = categoryRegs?.marpolRegulation;

    // Dropdown options
    const fosfaOptions = [
        { value: 2, text: 'Banned' },
        { value: 3, text: 'Restricted' },
        { value: 1, text: 'Listed' },
        { value: 0, text: 'Not Listed' },
    ];

    const euOptions = [
        { value: 2, text: 'Banned' },
        { value: 1, text: 'Listed' },
        { value: 0, text: 'Not Listed' },
    ];

    const niopOptions = [
        { value: 2, text: 'Restricted' },
        { value: 3, text: 'Prohibited' },
        { value: 1, text: 'Listed' },
        { value: 0, text: 'Not Listed' },
    ];

    const yesNoOptions = [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' },
    ];

    // Fire Protection checkbox state (A=1, B=2, C=3, D=4)
    const [fireProtectionA, setFireProtectionA] = useState(false);
    const [fireProtectionB, setFireProtectionB] = useState(false);
    const [fireProtectionC, setFireProtectionC] = useState(false);
    const [fireProtectionD, setFireProtectionD] = useState(false);

    // Initialize checkbox state from product data
    useEffect(() => {
        const fireProtection = ibc?.fireProtection || [];
        setFireProtectionA(fireProtection.includes(1));
        setFireProtectionB(fireProtection.includes(2));
        setFireProtectionC(fireProtection.includes(3));
        setFireProtectionD(fireProtection.includes(4));
    }, [ibc?.fireProtection]);

    // Handle Fire Protection checkbox changes
    const handleFireProtectionChange = useCallback(
        (value: number, checked: boolean) => {
            if (!mergePendingChanges || !isProductCategory) return;

            const currentFireProtection = ibc?.fireProtection || [];
            let updatedFireProtection: number[];

            if (checked) {
                // Add value if not present
                updatedFireProtection = currentFireProtection.includes(value) ? currentFireProtection : [...currentFireProtection, value].sort();
            } else {
                // Remove value
                updatedFireProtection = currentFireProtection.filter((v) => v !== value);
            }

            // Update local state
            switch (value) {
                case 1:
                    setFireProtectionA(checked);
                    break;
                case 2:
                    setFireProtectionB(checked);
                    break;
                case 3:
                    setFireProtectionC(checked);
                    break;
                case 4:
                    setFireProtectionD(checked);
                    break;
            }

            // Update pending changes
            const currentPendingChanges = getPendingChanges ? getPendingChanges() : {};
            const currentCategory = currentPendingChanges.category || product.category;
            const currentCategoryRegulations = (currentCategory?.regulations || {}) as any;
            const currentIbcRegulation = currentCategoryRegulations.ibcRegulation || ibc || {};

            const updatedCategory = {
                ...currentCategory,
                regulations: {
                    ...currentCategoryRegulations,
                    ibcRegulation: {
                        ...currentIbcRegulation,
                        fireProtection: updatedFireProtection,
                    },
                },
            };

            console.log('[RulesRegulationsSection] Fire Protection changed:', updatedFireProtection);
            mergePendingChanges({ category: updatedCategory });
        },
        [mergePendingChanges, getPendingChanges, ibc, isProductCategory, product.category]
    );

    // Helper: Check if tripartite has any values
    const hasTripartiteValue = (): boolean => {
        if (!tripartite) return false;
        return !!(tripartite.reportingCountry || tripartite.expiryDate || tripartite.countries);
    };

    return (
        <SidebarCard>
            <SidebarTitle>Rules and Regulations</SidebarTitle>
            <SidebarContent>
                <SidebarItem>
                    <label>USCG Group:</label>
                    {showEditUI ? (
                        <CompactSelect aria-label="USCG Group" value={regs?.uscgGroup?.id || ''} onChange={() => {}}>
                            <option value="">-- Select USCG Group --</option>
                            {Array.isArray(uscgGroups) &&
                                uscgGroups.map((group) => (
                                    <option key={group.id} value={group.id}>
                                        {group.number} - {group.name}
                                    </option>
                                ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatUSCGGroup(regs?.uscgGroup)}</span>
                    )}
                </SidebarItem>
                <SidebarItem>
                    <label>IMDG Class:</label>
                    {showEditUI ? (
                        <CompactSelect aria-label="IMDG Class" value={regs?.imdgCodeClass?.id || ''} onChange={() => {}}>
                            <option value="">-- Select IMDG Code Classification --</option>
                            {Array.isArray(imdgCodeClasses) &&
                                imdgCodeClasses.map((imdgClass) => (
                                    <option key={imdgClass.id} value={imdgClass.id}>
                                        {imdgClass.classification} - {imdgClass.description}
                                    </option>
                                ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatIMDGClass(regs?.imdgCodeClass)}</span>
                    )}
                </SidebarItem>
                <SidebarItem>
                    <label>FOSFA:</label>
                    {showEditUI ? (
                        <CompactSelect aria-label="FOSFA" value={regs?.fosfaAllowance ?? ''} onChange={() => {}}>
                            {fosfaOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.text}
                                </option>
                            ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatAllowance(regs?.fosfaAllowance)}</span>
                    )}
                </SidebarItem>
                <SidebarItem>
                    <label>EU:</label>
                    {showEditUI ? (
                        <CompactSelect aria-label="EU" value={regs?.euAllowance ?? ''} onChange={() => {}}>
                            {euOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.text}
                                </option>
                            ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatAllowance(regs?.euAllowance)}</span>
                    )}
                </SidebarItem>
                <SidebarItem>
                    <label>NIOP:</label>
                    {showEditUI ? (
                        <CompactSelect aria-label="NIOP" value={regs?.niop1Allowance ?? ''} onChange={() => {}}>
                            {niopOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.text}
                                </option>
                            ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatNIOPAllowance(regs?.niop1Allowance)}</span>
                    )}
                </SidebarItem>
                {(showEditUI || regs?.isOpa90) && (
                    <SidebarItem>
                        <label>OPA90:</label>
                        {showEditUI ? (
                            <CompactSelect
                                aria-label="OPA90"
                                value={regs?.isOpa90 === true ? 'true' : regs?.isOpa90 === false ? 'false' : ''}
                                onChange={() => {}}
                            >
                                {yesNoOptions.map((opt) => (
                                    <option key={opt.value.toString()} value={opt.value.toString()}>
                                        {opt.text}
                                    </option>
                                ))}
                            </CompactSelect>
                        ) : (
                            <span>{formatYesNo(regs?.isOpa90)}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Tripartite Agreement Section */}
                {(showEditUI || hasTripartiteValue()) && (
                    <>
                        <SidebarTitle style={{ fontSize: '0.9rem', marginTop: '1rem' }}>Tripartite Agreement</SidebarTitle>
                        <SidebarItem>
                            <label>Reporting Country:</label>
                            {showEditUI ? (
                                <CompactInput100 value={tripartite?.reportingCountry || ''} onChange={() => {}} />
                            ) : (
                                <span>{tripartite?.reportingCountry || ''}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem>
                            <label>Expiry Date:</label>
                            {showEditUI ? (
                                <CompactInput100 value={tripartite?.expiryDate || ''} onChange={() => {}} />
                            ) : (
                                <span>{formatDate(tripartite?.expiryDate)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem>
                            <label>Countries:</label>
                            {showEditUI ? (
                                <CompactInput100 value={tripartite?.countries || ''} onChange={() => {}} />
                            ) : (
                                <span>{tripartite?.countries || ''}</span>
                            )}
                        </SidebarItem>
                    </>
                )}

                {/* IBC Annex II - Chapter 17 Section */}
                {(marpolReg === 2 || marpolReg === 3) && (
                    <>
                        <SidebarTitle style={{ fontSize: '0.9rem', marginTop: '1rem' }}>IBC {formatMarpolRegulation(marpolReg)}</SidebarTitle>
                        <SidebarItem>
                            <label>Pollution Category{marpolReg === 2 ? ' (c)' : ''}:</label>
                            <span>{formatPollutionCategory(categoryRegs?.marpolPollutionCategory)}</span>
                        </SidebarItem>
                    </>
                )}

                {marpolReg === 2 && ibc && (
                    <>
                        <SidebarItem>
                            <label>Hazard Rules (d):</label>
                            <span>{formatIBCHazards(ibc.hazards)}</span>
                        </SidebarItem>
                        <SidebarItem>
                            <label>Ship Type (e):</label>
                            <span>{formatShipType(ibc.shipType)}</span>
                        </SidebarItem>
                        <SidebarItem>
                            <label>Tank Type (f):</label>
                            <span>{formatTankType(ibc.tankType)}</span>
                        </SidebarItem>
                        <SidebarItem>
                            <label>Tank Ventilation (g):</label>
                            <span>{formatTankVents(ibc.tankVents)}</span>
                        </SidebarItem>
                        <SidebarItem>
                            <label>Environment Control (h):</label>
                            <span>{formatEnvironmentalControl(ibc.tankEnvironmentalControl)}</span>
                        </SidebarItem>
                        <SidebarItem>
                            <label>Gauging (j):</label>
                            <span>{formatGauging(ibc.gauging)}</span>
                        </SidebarItem>
                        <SidebarItem>
                            <label>Vapour Detection (k):</label>
                            <span>{formatVapourDetection(ibc.vapourDetection)}</span>
                        </SidebarItem>
                        <SidebarItem>
                            <label>Fire Protection (l):</label>
                            {showEditUI && isProductCategory ? (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fireProtectionA}
                                            onChange={(e) => handleFireProtectionChange(1, e.target.checked)}
                                        />
                                        A
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fireProtectionB}
                                            onChange={(e) => handleFireProtectionChange(2, e.target.checked)}
                                        />
                                        B
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fireProtectionC}
                                            onChange={(e) => handleFireProtectionChange(3, e.target.checked)}
                                        />
                                        C
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fireProtectionD}
                                            onChange={(e) => handleFireProtectionChange(4, e.target.checked)}
                                        />
                                        D
                                    </label>
                                </div>
                            ) : (
                                <span>{formatFireProtection(ibc.fireProtection)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem>
                            <label>Emergency Equipment (n):</label>
                            <span>{formatYesNo(ibc.requiresEmergencyEquipment)}</span>
                        </SidebarItem>
                    </>
                )}
            </SidebarContent>
        </SidebarCard>
    );
};

export default RulesRegulationsSection;
